import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { ArrowLeft, Palette, CreditCard, LogOut, Loader2, ShieldCheck, FileText, ChevronRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { userApi } from "../lib/api";
import { useTheme } from "../context/ThemeContext";
import { toast } from "sonner";
import ProfileSection from "../components/settings/ProfileSection";
import PasswordSection from "../components/settings/PasswordSection";
import PaymentMethodsSection from "../components/settings/PaymentMethodsSection";
import { BottomNav } from "../components/BottomNav";
import { DesktopSidebar } from "../components/DesktopSidebar";

const CURRENCIES = [
  { code: "usd", label: "USD ($)" },
  { code: "eur", label: "EUR (\u20ac)" },
  { code: "gbp", label: "GBP (\u00a3)" },
];

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, logout, refreshUser, updateUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const [currency, setCurrency] = useState(user?.default_currency || "usd");
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [savingTheme, setSavingTheme] = useState(false);

  useEffect(() => {
    if (user) {
      setCurrency(user.default_currency || "usd");
    }
  }, [user]);

  useEffect(() => {
    // Pull changes made by the native app whenever the web profile is opened.
    refreshUser();
  }, [refreshUser]);

  const handleSavePreferences = async () => {
    setSavingPrefs(true);
    try {
      await userApi.updatePreferences({
        theme,
        default_currency: currency,
      });
      updateUser({ default_currency: currency, theme });
      toast.success("Preferences saved");
    } catch {
      toast.error("Failed to save preferences");
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleThemeChange = async (checked) => {
    const nextTheme = checked ? "dark" : "light";
    const previousTheme = theme;
    setTheme(nextTheme);
    setSavingTheme(true);
    try {
      await userApi.updatePreferences({ theme: nextTheme });
      updateUser({ theme: nextTheme });
    } catch {
      setTheme(previousTheme);
      toast.error("Failed to update appearance");
    } finally {
      setSavingTheme(false);
    }
  };

  const handleLogout = async () => {
    const serverLogoutSucceeded = await logout();
    navigate("/");
    if (serverLogoutSucceeded) {
      toast.success("Signed out");
    } else {
      toast.warning("Signed out locally. Please refresh if this device still appears logged in.");
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20 lg:pl-64">
      <DesktopSidebar active="/settings" />
      <div className="fixed inset-0 paper-texture pointer-events-none" />

      <header className="sticky top-0 z-20 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-2xl mx-auto flex items-center gap-3 p-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/explore")}
            className="rounded-xl"
            data-testid="settings-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-bold">Profile</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4 space-y-6 relative z-10 pb-24">
        <ProfileSection user={user} updateUser={updateUser} />
        <PaymentMethodsSection />

        {/* Payment Currency */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg">Payment Currency</CardTitle>
            </div>
            <CardDescription>Currency used for purchases</CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger className="h-12 text-base" data-testid="currency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Theme */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Palette className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg">Appearance</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-medium">Dark Mode</Label>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {theme === "dark" ? "Dark theme active" : "Light theme active"}
                </p>
              </div>
              <Switch
                checked={theme === "dark"}
                onCheckedChange={handleThemeChange}
                disabled={savingTheme}
                data-testid="dark-mode-toggle"
              />
            </div>
          </CardContent>
        </Card>

        <PasswordSection />

        {/* Legal */}
        <Card data-testid="profile-legal-section">
          <CardHeader>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg">Legal</CardTitle>
            </div>
            <CardDescription>Policies governing your account and use of Culture Ken</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Link
              to="/privacy"
              className="flex items-center gap-3 rounded-md border border-border px-4 h-12 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="profile-privacy-policy-link"
            >
              <ShieldCheck className="w-4 h-4 text-primary" />
              <span className="flex-1 font-medium">Privacy Policy</span>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </Link>
            <Link
              to="/terms"
              className="flex items-center gap-3 rounded-md border border-border px-4 h-12 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="profile-terms-of-use-link"
            >
              <FileText className="w-4 h-4 text-primary" />
              <span className="flex-1 font-medium">Terms of Use</span>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </Link>
          </CardContent>
        </Card>

        {/* Save Preferences */}
        <Button
          className="w-full h-12 rounded-xl text-base font-medium"
          onClick={handleSavePreferences}
          disabled={savingPrefs}
          data-testid="save-settings-btn"
        >
          {savingPrefs ? (
            <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Saving...</>
          ) : (
            "Save Preferences"
          )}
        </Button>

        {/* Sign Out */}
        <Button
          variant="outline"
          className="w-full h-12 rounded-xl text-base font-medium text-destructive hover:text-destructive"
          onClick={handleLogout}
          data-testid="signout-btn"
        >
          <LogOut className="w-5 h-5 mr-2" />
          Sign Out
        </Button>
      </main>
      <BottomNav active="/settings" />
    </div>
  );
};

export default SettingsPage;
